import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import CompraPendienteHiloInfoDialog from '../../components/compras/CompraPendienteHiloInfoDialog';
import SearchableSelect from '../../components/SearchableSelect';
import ProveedorSelectorField from '../../components/ProveedorSelectorField';
import ObservacionesConNotaProveedor from '../../components/ObservacionesConNotaProveedor';
import UbicacionSelector from '../../components/UbicacionSelector';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import { useSucursalAltaFormulario } from '../../hooks/useSucursalAltaFormulario';
import { persistGlobalNroSuc } from '../../hooks/useSucursalesVentaFiltro';
import productoService from '../../services/productoService';
import {
  cargarProductoParaDocumento,
  clearRechazoDiscontinuado,
  esDiscontinuadoBloqueadoParaAgregar,
  esTipoDiscontinuado,
  evaluarUsoProductoDocumento,
  mensajeArticuloDiscontinuadoBloqueado,
  paramsBusquedaProductoDocumento,
  type ModoBloqueoArtDiscontinuados,
} from '../../utils/bloqueaArtDiscontinuados';
import remitoCompraService, { mapRemitoCompraItemToDto } from '../../services/remitoCompraService';
import facturaCompraService, { FacturaCompra } from '../../services/facturaCompraService';
import comprasSeguimientoService, {
  CompraPendienteMaster,
  esProductoInventariado,
  filtrarCompraPendientesPorBusqueda,
  filtrarCompraPendientesPorFechaEmision,
  formatComprobanteMasterDisplay,
  formatFechaEmisionComprobanteMaster,
  precioRemitoDesdePendienteMaster,
} from '../../services/comprasSeguimientoService';
import ItemNotasTextField from '../../components/ItemNotasTextField';
import { getApiErrorMessage } from '../../utils/apiError';
import { normalizeItemNotas } from '../../utils/formatters';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';

interface RemitoCompraItemForm {
  /** Id estable de fila en UI (no se envía al API). */
  rowId: number;
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  porcDesc: number;
  um?: string;
  notas: string;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
}

interface RemitoCompraFormularioForm {
  nroSuc: number;
  letra: string;
  nroComprobante: number;
  nroProveedor: number;
  nombre?: string;
  cuit?: string;
  descripcion?: string;

  fechaEmision: string;
  fechaVencim?: string;

  nroUbicacionDestino?: number;
}

/** Factura de compra (tipos 1/2) cuando el pendiente apunta directamente al comprobante FC. */
const nroFormularioFacturaDesdePendiente = (p: CompraPendienteMaster): number | undefined => {
  const tipo = String(p.nroTipoFormularioMaster ?? '').trim();
  if (tipo === '1' || tipo === '2') {
    const nro = Number(p.nroFormularioMaster);
    return nro > 0 ? nro : undefined;
  }
  return undefined;
};

/**
 * Notas de línea para el remito: prioriza las del pendiente;
 * si el master es FC y no trae notas, las toma del detalle de la factura.
 */
const resolverNotasDesdePendienteCompra = async (
  p: CompraPendienteMaster,
  facturasCache: Map<number, FacturaCompra>,
): Promise<string> => {
  const desdePendiente = normalizeItemNotas(p.notas ?? null);
  if (desdePendiente.trim()) return desdePendiente;

  const nroFc = nroFormularioFacturaDesdePendiente(p);
  if (!nroFc) return desdePendiente;

  let factura = facturasCache.get(nroFc);
  if (!factura) {
    try {
      factura = await facturaCompraService.getFacturaCompra(nroFc);
      facturasCache.set(nroFc, factura);
    } catch {
      return desdePendiente;
    }
  }

  const detalle = (factura.detalles ?? []).find((d) => Number(d.nroItem) === Number(p.nroItemMaster));
  return normalizeItemNotas(detalle?.notas ?? null);
};

const defaultItem: Omit<RemitoCompraItemForm, 'rowId'> = {
  codigoArticulo: '',
  descripcion: '',
  cantidad: 1,
  precioUnitario: 0,
  porcDesc: 0,
  um: '',
  notas: '',
};

const todayIsoDate = (): string => new Date().toISOString().split('T')[0];

const normalizeCodigoArticulo = (codigo: string | null | undefined): string =>
  String(codigo ?? '').trim().toLowerCase();

const isPendienteEnRemito = (
  pendiente: CompraPendienteMaster,
  remitoItems: RemitoCompraItemForm[],
): boolean =>
  remitoItems.some(
    (it) =>
      it.nroFormularioMaster === pendiente.nroFormularioMaster &&
      it.nroItemMaster === pendiente.nroItemMaster,
  );

const RemitoCompraForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const compraPendienteFromNav = (location.state as { compraPendiente?: CompraPendienteMaster } | null)?.compraPendiente;
  const compraPendienteNavAppliedRef = useRef(false);
  const agregarPendienteRef = useRef<(p: CompraPendienteMaster) => Promise<void>>(async () => {});
  /** Evita carrera: un 2º "Agregar todos" puede reinyectar líneas ya borradas al terminar. */
  const agregandoTodosRef = useRef(false);
  const itemRowIdRef = useRef(0);
  /** Masters quitados a mano; un "Agregar todos" en vuelo no debe volver a insertarlos. */
  const mastersEliminadosRef = useRef<Set<string>>(new Set());

  const [formulario, setFormulario] = useState<RemitoCompraFormularioForm>({
    nroSuc: 0,
    letra: 'R',
    nroComprobante: 0,
    nroProveedor: 0,
    nombre: '',
    cuit: '',
    descripcion: '',
    fechaEmision: todayIsoDate(),
    fechaVencim: todayIsoDate(),
    nroUbicacionDestino: undefined,
  });

  const { sucursales, loading: loadingSucursales } = useSucursalesUsuarioContext();
  const sucursalesOrdenadas = useMemo(
    () => [...sucursales].sort((a, b) => a.nroSuc - b.nroSuc),
    [sucursales],
  );

  const setNroSucFormulario = useCallback((nroSuc: number) => {
    setFormulario((prev) => (Number(prev.nroSuc) === nroSuc ? prev : { ...prev, nroSuc }));
  }, []);

  const { onSucursalChange } = useSucursalAltaFormulario({
    nroSuc: Number(formulario.nroSuc) || 0,
    setNroSuc: setNroSucFormulario,
    sucursales,
    loadingSucursales,
  });

  useEffect(() => {
    const v = Number(formulario.nroSuc);
    if (Number.isFinite(v) && v > 0) {
      persistGlobalNroSuc(v);
    }
  }, [formulario.nroSuc]);

  const [proveedorNombre, setProveedorNombre] = useState('');
  const [ubicacionNombre, setUbicacionNombre] = useState('');

  const [items, setItems] = useState<RemitoCompraItemForm[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const [saving, setSaving] = useState(false);

  // Producto search modal
  const [openProductSearch, setOpenProductSearch] = useState(false);
  const [productSearchIndex, setProductSearchIndex] = useState<number | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productos, setProductos] = useState<any[]>([]);
  const [modoBloqueoDis, setModoBloqueoDis] = useState<ModoBloqueoArtDiscontinuados>(1);
  const [productosPage, setProductosPage] = useState(0); // 0-based
  const [productosRowsPerPage] = useState(10);
  const [productosTotalCount, setProductosTotalCount] = useState(0);
  const [openPendientes, setOpenPendientes] = useState(false);
  const [pendientes, setPendientes] = useState<CompraPendienteMaster[]>([]);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [agregandoTodos, setAgregandoTodos] = useState(false);
  const [pendientesBusqueda, setPendientesBusqueda] = useState('');
  const [pendientesFechaDesde, setPendientesFechaDesde] = useState('');
  const [pendientesFechaHasta, setPendientesFechaHasta] = useState('');
  const [soloInventariados, setSoloInventariados] = useState(true);
  const [pendienteHiloInfo, setPendienteHiloInfo] = useState<{
    nroFormularioMaster: number;
    nroItemMaster: number;
    resumenLinea?: string;
  } | null>(null);

  const codigosArticuloEnRemito = useMemo(() => {
    const codigos = new Set<string>();
    items.forEach((it) => {
      const codigo = normalizeCodigoArticulo(it.codigoArticulo);
      if (codigo) codigos.add(codigo);
    });
    return codigos;
  }, [items]);

  const productosDisponibles = useMemo(
    () =>
      productos.filter((p) => {
        const codigo = normalizeCodigoArticulo(p.codigoArticulo || p.codigo);
        return codigo && !codigosArticuloEnRemito.has(codigo);
      }),
    [productos, codigosArticuloEnRemito],
  );

  const pendientesDisponibles = useMemo(
    () => pendientes.filter((p) => !isPendienteEnRemito(p, items)),
    [pendientes, items],
  );

  const pendientesFiltrados = useMemo(() => {
    let rows = pendientesDisponibles;
    if (soloInventariados) {
      rows = rows.filter((p) => esProductoInventariado(p.invetariado));
    }
    rows = filtrarCompraPendientesPorFechaEmision(rows, pendientesFechaDesde, pendientesFechaHasta);
    return filtrarCompraPendientesPorBusqueda(rows, pendientesBusqueda);
  }, [pendientesDisponibles, soloInventariados, pendientesBusqueda, pendientesFechaDesde, pendientesFechaHasta]);

  const handleFormularioChange = (updates: Partial<RemitoCompraFormularioForm>) => {
    setFormulario((f) => ({ ...f, ...updates }));
  };

  const handleItemChange = (idx: number, updates: Partial<RemitoCompraItemForm>) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...updates };
      return next;
    });
  };

  const masterKey = (nroFormularioMaster?: number, nroItemMaster?: number): string | null => {
    if (!nroFormularioMaster || !nroItemMaster) return null;
    return `${nroFormularioMaster}-${nroItemMaster}`;
  };

  const removeItem = (rowId: number) => {
    setItems((prev) => {
      const removed = prev.find((it) => it.rowId === rowId);
      const key = masterKey(removed?.nroFormularioMaster, removed?.nroItemMaster);
      if (key) mastersEliminadosRef.current.add(key);
      return prev.filter((it) => it.rowId !== rowId);
    });
  };

  const nextRowId = () => {
    itemRowIdRef.current += 1;
    return itemRowIdRef.current;
  };

  const addItem = () => setItems((prev) => [...prev, { ...defaultItem, rowId: nextRowId() }]);

  const handleProveedorSelect = (prov: any) => {
    setFormulario((f) => ({
      ...f,
      nroProveedor: prov.nroProveedor,
      nombre: prov.nombre,
      cuit: prov.cuit,
    }));
    setProveedorNombre(prov.nombre || '');
  };

  const handleProveedorClear = () => {
    setFormulario((f) => ({
      ...f,
      nroProveedor: 0,
      nombre: '',
      cuit: '',
    }));
    setProveedorNombre('');
  };

  const loadProductos = useCallback(async () => {
    if (!openProductSearch) return;
    try {
      const paramsBloqueo = await paramsBusquedaProductoDocumento('COMPRAS');
      setModoBloqueoDis(paramsBloqueo.modoBloqueoArtDiscontinuados ?? 1);
      const res = await productoService.getProductos({
        searchTerm: productSearchTerm || undefined,
        pageNumber: productosPage + 1,
        pageSize: productosRowsPerPage,
        ...paramsBloqueo,
      });
      setProductos(res.items || []);
      setProductosTotalCount(res.totalCount || 0);
    } catch {
      setProductos([]);
      setProductosTotalCount(0);
    }
  }, [openProductSearch, productSearchTerm, productosPage, productosRowsPerPage]);

  useEffect(() => {
    if (openProductSearch) loadProductos();
  }, [openProductSearch, loadProductos]);

  useEffect(() => {
    if (compraPendienteNavAppliedRef.current) return;
    if (!compraPendienteFromNav) return;

    compraPendienteNavAppliedRef.current = true;
    const p = compraPendienteFromNav;
    const np = p.nroProveedor;
    if (np && np > 0) {
      setFormulario((prev) => ({
        ...prev,
        nroProveedor: np,
        nombre: p.nombreProveedor || prev.nombre,
      }));
      setProveedorNombre(p.nombreProveedor || '');
    }
    void agregarPendienteRef.current(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compraPendienteFromNav]);

  const handleSelectProduct = async (p: any) => {
    const idx = productSearchIndex;
    if (idx === null) return;
    const codigo = p.codigoArticulo || p.codigo || '';
    clearRechazoDiscontinuado('COMPRAS', codigo);
    if (esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modoBloqueoDis)) {
      enqueueSnackbar(mensajeArticuloDiscontinuadoBloqueado(codigo), { variant: 'warning' });
      return;
    }
    const decision = await evaluarUsoProductoDocumento(
      {
        codigoArticulo: codigo,
        tipoArticulo: p.tipoArticulo,
        descripcion: p.descripcion || p.descripcionArticulo,
      },
      'COMPRAS',
    );
    if (!decision.permitido) {
      if (decision.motivo !== 'discontinuado_rechazado') {
        enqueueSnackbar(decision.mensaje, { variant: 'warning' });
      }
      return;
    }
    const desc = p.descripcion || p.descripcionArticulo || '';
    const costo = Number(p.costo) || 0;
    let um = String(p.idUnidadMedida || p.um || '').trim();
    try {
      const umRes = await productoService.getUmCompra(String(codigo), formulario.nroProveedor || null);
      um = umRes.umCompra || um;
    } catch {
      /* ignore */
    }
    setItems((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        codigoArticulo: String(codigo),
        descripcion: String(desc),
        precioUnitario: costo,
        um,
      };
      return next;
    });
    setOpenProductSearch(false);
    setProductSearchIndex(null);
  };

  const handleCodigoBlurOrEnter = useCallback(
    async (idx: number, codigoValue: string) => {
      const codigo = (codigoValue || '').trim();
      if (!codigo) return;
      try {
        const modo = (await paramsBusquedaProductoDocumento('COMPRAS')).modoBloqueoArtDiscontinuados ?? 1;
        setModoBloqueoDis(modo);
        const p = await cargarProductoParaDocumento(codigo, 'COMPRAS');
        if (esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modo)) {
          enqueueSnackbar(mensajeArticuloDiscontinuadoBloqueado(codigo), { variant: 'warning' });
          return;
        }
        const decision = await evaluarUsoProductoDocumento(
          {
            codigoArticulo: codigo,
            tipoArticulo: p.tipoArticulo,
            descripcion: (p as any).descripcion || (p as any).descripcionArticulo,
          },
          'COMPRAS',
        );
        if (!decision.permitido) {
          if (decision.motivo === 'discontinuado_rechazado') {
            handleItemChange(idx, { codigoArticulo: '', descripcion: '', precioUnitario: 0, um: '' });
          } else {
            enqueueSnackbar(decision.mensaje, { variant: 'warning' });
          }
          return;
        }
        const desc = (p as any).descripcion || (p as any).descripcionArticulo || '';
        const costo = Number((p as any).costo) || Number((p as any).costoBrutoUnitario) || 0;
        let um = String((p as any).idUnidadMedida || (p as any).um || '').trim();
        try {
          const umRes = await productoService.getUmCompra(codigo, formulario.nroProveedor || null);
          um = umRes.umCompra || um;
        } catch {
          /* ignore */
        }
        handleItemChange(idx, { codigoArticulo: codigo, descripcion: desc, precioUnitario: costo, um });
      } catch {
        enqueueSnackbar(`No se encontró el producto con código "${codigo}"`, { variant: 'warning' });
      }
    },
    [enqueueSnackbar, formulario.nroProveedor]
  );

  const handleSubmit = async () => {
    if (saving) return;
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Debe seleccionar un proveedor', { variant: 'warning' });
      return;
    }
    if (!formulario.nroSuc || formulario.nroSuc <= 0) {
      enqueueSnackbar('Debe seleccionar una sucursal válida', { variant: 'warning' });
      return;
    }
    if (!loadingSucursales && sucursalesOrdenadas.length === 0) {
      enqueueSnackbar('No tiene sucursales asignadas. Solicite asignación a un administrador.', { variant: 'warning' });
      return;
    }
    if (!formulario.letra?.trim()) {
      enqueueSnackbar('Debe seleccionar una letra', { variant: 'warning' });
      return;
    }
    if (!formulario.nroComprobante || formulario.nroComprobante <= 0) {
      enqueueSnackbar('Debe ingresar un número de comprobante válido', { variant: 'warning' });
      return;
    }
    if (!formulario.nroUbicacionDestino || formulario.nroUbicacionDestino <= 0) {
      enqueueSnackbar('Debe seleccionar una ubicación destino válida', { variant: 'warning' });
      return;
    }

    const itemsValidos = itemsRef.current.filter(
      (it) => (it.codigoArticulo || '').trim().length > 0 && Number(it.cantidad) > 0
    );

    if (itemsValidos.length === 0) {
      enqueueSnackbar('Debe agregar al menos un ítem válido (código y cantidad > 0)', { variant: 'warning' });
      return;
    }

    const payload = {
      formulario: {
        nroSuc: Number(formulario.nroSuc),
        letra: formulario.letra?.trim() || 'A',
        nroComprobante: Number(formulario.nroComprobante),
        nroProveedor: Number(formulario.nroProveedor),
        nombre: formulario.nombre?.trim() || undefined,
        cuit: formulario.cuit?.trim() || undefined,
        descripcion: formulario.descripcion?.trim() || undefined,
        fechaEmision: formulario.fechaEmision || todayIsoDate(),
        fechaVencim: formulario.fechaVencim || undefined,
        nroUbicacionDestino: formulario.nroUbicacionDestino,
      },
      items: itemsValidos.map((it) =>
        mapRemitoCompraItemToDto({
          codigoArticulo: it.codigoArticulo,
          descripcion: it.descripcion,
          cantidad: Number(it.cantidad),
          precioUnitario: Number(it.precioUnitario),
          porcDesc: Number(it.porcDesc ?? 0),
          um: it.um,
          notas: it.notas,
          nroFormularioMaster: it.nroFormularioMaster,
          nroItemMaster: it.nroItemMaster,
        })
      ),
    };

    try {
      setSaving(true);
      const created = await remitoCompraService.createRemitoCompra(payload as any);
      enqueueSnackbar('Remito de compra creado correctamente', { variant: 'success' });
      const hayProductos = itemsValidos.some((it) => !!(it.codigoArticulo || '').trim());
      navigate(`/compras/remitos/${created.nroFormulario}`, {
        state: { pedirEtiquetas: hayProductos },
      });
    } catch (error: unknown) {
      let msg = getApiErrorMessage(error, 'Error al crear el remito de compra');
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 409 && /formulario/i.test(msg)) {
        msg = `${msg}. Verifique que el comprobante (letra/sucursal/número) no exista ya para ese proveedor e intente nuevamente.`;
      }
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const openBuscarPendientes = async () => {
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Primero seleccione un proveedor', { variant: 'warning' });
      return;
    }
    setPendientesBusqueda('');
    setPendientesFechaDesde('');
    setPendientesFechaHasta('');
    setSoloInventariados(true);
    setOpenPendientes(true);
    setLoadingPendientes(true);
    try {
      const data = await comprasSeguimientoService.getPendientesMaster(formulario.nroProveedor, true);
      const rows = (data || []).filter((p) => (Number(p.pendienteRecibir) || 0) > 0);
      setPendientes(rows);
      if (rows.length === 0) {
        enqueueSnackbar('No hay líneas con pendiente de recibir para este proveedor', { variant: 'info' });
      }
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron cargar pendientes'), { variant: 'error' });
      setPendientes([]);
    } finally {
      setLoadingPendientes(false);
    }
  };

  const agregarPendiente = async (p: CompraPendienteMaster) => {
    const pendienteRecibir = Number(p.pendienteRecibir) || 0;
    const cantidadSugerida = pendienteRecibir > 0 ? pendienteRecibir : 1;
    let { precioUnitario, porcDesc } = precioRemitoDesdePendienteMaster(p);
    if (precioUnitario <= 0 && (p.codigoArticulo || '').trim()) {
      try {
        const pr = await cargarProductoParaDocumento((p.codigoArticulo || '').trim(), 'COMPRAS');
        precioUnitario = Number((pr as any).costo) || Number((pr as any).costoBrutoUnitario) || 0;
        porcDesc = 0;
      } catch {
        /* sin precio en OC ni maestro */
      }
    }
    const key = masterKey(p.nroFormularioMaster, p.nroItemMaster);
    if (key) mastersEliminadosRef.current.delete(key);

    const notas = await resolverNotasDesdePendienteCompra(p, new Map());
    setItems((prev) => {
      const exists = prev.some(
        (it) =>
          it.nroFormularioMaster === p.nroFormularioMaster &&
          it.nroItemMaster === p.nroItemMaster
      );
      if (exists) return prev;
      return [
        ...prev,
        {
          ...defaultItem,
          rowId: nextRowId(),
          codigoArticulo: p.codigoArticulo || '',
          descripcion: p.descripcionArticulo || '',
          cantidad: cantidadSugerida,
          precioUnitario,
          porcDesc,
          notas,
          nroFormularioMaster: p.nroFormularioMaster,
          nroItemMaster: p.nroItemMaster,
        },
      ];
    });
  };
  agregarPendienteRef.current = agregarPendiente;

  const agregarTodosPendientes = async () => {
    if (agregandoTodosRef.current) return;
    if (!pendientesFiltrados.length) return;

    agregandoTodosRef.current = true;
    setAgregandoTodos(true);

    const pendientesAAgregar = [...pendientesFiltrados];
    // Intención explícita de volver a cargar estos masters (anula borrados previos).
    for (const p of pendientesAAgregar) {
      const key = masterKey(p.nroFormularioMaster, p.nroItemMaster);
      if (key) mastersEliminadosRef.current.delete(key);
    }

    const keysExistentes = new Set(
      itemsRef.current
        .map((it) => masterKey(it.nroFormularioMaster, it.nroItemMaster))
        .filter((k): k is string => !!k),
    );
    const nuevosItems: RemitoCompraItemForm[] = [];
    let agregadas = 0;
    let omitidas = 0;
    const facturasCache = new Map<number, FacturaCompra>();

    try {
      for (const p of pendientesAAgregar) {
        const key = masterKey(p.nroFormularioMaster, p.nroItemMaster) ?? '';
        if (key && keysExistentes.has(key)) {
          omitidas += 1;
          continue;
        }

        const pendienteRecibir = Number(p.pendienteRecibir) || 0;
        const cantidadSugerida = pendienteRecibir > 0 ? pendienteRecibir : 1;
        let { precioUnitario, porcDesc } = precioRemitoDesdePendienteMaster(p);
        if (precioUnitario <= 0 && (p.codigoArticulo || '').trim()) {
          try {
            const pr = await cargarProductoParaDocumento((p.codigoArticulo || '').trim(), 'COMPRAS');
            precioUnitario = Number((pr as any).costo) || Number((pr as any).costoBrutoUnitario) || 0;
            porcDesc = 0;
          } catch {
            /* sin precio en OC ni maestro */
          }
        }

        const notas = await resolverNotasDesdePendienteCompra(p, facturasCache);
        nuevosItems.push({
          ...defaultItem,
          rowId: nextRowId(),
          codigoArticulo: p.codigoArticulo || '',
          descripcion: p.descripcionArticulo || '',
          cantidad: cantidadSugerida,
          precioUnitario,
          porcDesc,
          notas,
          nroFormularioMaster: p.nroFormularioMaster,
          nroItemMaster: p.nroItemMaster,
        });
        if (key) keysExistentes.add(key);
        agregadas += 1;
      }

      if (agregadas === 0) {
        enqueueSnackbar('No se pudo agregar ninguna línea desde pendientes', { variant: 'info' });
        return;
      }

      // No reinyecta masters ya presentes ni los que el usuario borró mientras este proceso corría.
      setItems((prev) => {
        const keys = new Set(
          prev
            .map((it) => masterKey(it.nroFormularioMaster, it.nroItemMaster))
            .filter((k): k is string => !!k),
        );
        const toAdd = nuevosItems.filter((it) => {
          const key = masterKey(it.nroFormularioMaster, it.nroItemMaster);
          if (!key) return true;
          if (mastersEliminadosRef.current.has(key)) return false;
          if (keys.has(key)) return false;
          keys.add(key);
          return true;
        });
        return toAdd.length ? [...prev, ...toAdd] : prev;
      });
      setOpenPendientes(false);
      enqueueSnackbar(
        omitidas > 0
          ? `Se agregaron ${agregadas} líneas. ${omitidas} quedaron omitidas.`
          : `Se agregaron ${agregadas} líneas desde pendientes.`,
        { variant: 'success' },
      );
    } finally {
      agregandoTodosRef.current = false;
      setAgregandoTodos(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/compras/remitos')}
              sx={{ textTransform: 'none' }}
            >
              Remitos de Compra
            </Button>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '1.35rem', sm: '2.125rem' } }}
            >
              Nuevo Remito de Compra
            </Typography>
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Encabezado
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="flex-end">
            <Grid item xs={12} md={6}>
              <ProveedorSelectorField
                label="Proveedor"
                required
                value={
                  formulario.nroProveedor > 0 && proveedorNombre
                    ? {
                        nroProveedor: formulario.nroProveedor,
                        nombre: proveedorNombre,
                        cuit: formulario.cuit || undefined,
                      }
                    : null
                }
                onSelect={handleProveedorSelect}
                onClear={handleProveedorClear}
              />
            </Grid>


            <Grid item xs={6} md={2}>
              <SearchableSelect
                label="Letra"
                required
                value={formulario.letra || 'R'}
                onChange={(v) => handleFormularioChange({ letra: v ?? 'R' })}
                options={[
                  { value: 'R', label: 'R' },
                  { value: 'X', label: 'X' },
                ]}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Sucursal"
                type="text"
                value={String(formulario.nroSuc ?? 0).padStart(5, '0')}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  const n = parseInt(v, 10);
                  const next =
                    v === '' || isNaN(n) ? 0 : Math.min(99999, Math.max(0, n));
                  onSucursalChange(next);
                }}
                inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
                required
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Nro. Comprobante"
                type="text"
                value={String(formulario.nroComprobante ?? 0).padStart(8, '0')}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  const n = parseInt(v, 10);
                  const next =
                    v === '' || isNaN(n) ? 0 : Math.min(99999999, Math.max(0, n));
                  handleFormularioChange({ nroComprobante: next });
                }}
                inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Fecha emisión"
                type="date"
                value={formulario.fechaEmision}
                onChange={(e) => handleFormularioChange({ fechaEmision: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Fecha vencimiento"
                type="date"
                value={formulario.fechaVencim || ''}
                onChange={(e) => handleFormularioChange({ fechaVencim: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <UbicacionSelector
                label="Ubicación destino"
                size="medium"
                value={ubicacionNombre}
                onChange={(label, nro) => {
                  setUbicacionNombre(label);
                  handleFormularioChange({ nroUbicacionDestino: nro });
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <ObservacionesConNotaProveedor nroProveedor={formulario.nroProveedor}>
                <TextField
                  fullWidth
                  label="Observaciones"
                  value={formulario.descripcion || ''}
                  onChange={(e) => handleFormularioChange({ descripcion: e.target.value })}
                />
              </ObservacionesConNotaProveedor>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Detalle
          </Typography>

          {isMobile ? (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {items.length === 0 ? (
                <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
                  Sin ítems. Use &quot;Agregar item&quot; para añadir líneas.
                </Typography>
              ) : (
                items.map((it, idx) => (
                  <Card key={it.rowId} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack spacing={1.25}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Ítem {idx + 1}
                          </Typography>
                          <IconButton size="small" onClick={() => removeItem(it.rowId)} color="error" aria-label="Eliminar ítem">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <TextField
                          fullWidth
                          size="small"
                          label="Código"
                          value={it.codigoArticulo}
                          onChange={(e) => {
                            clearRechazoDiscontinuado('COMPRAS', items[idx]?.codigoArticulo);
                            clearRechazoDiscontinuado('COMPRAS', e.target.value);
                            handleItemChange(idx, { codigoArticulo: e.target.value });
                          }}
                          onBlur={(e) => handleCodigoBlurOrEnter(idx, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value);
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setProductSearchIndex(idx);
                                    setOpenProductSearch(true);
                                  }}
                                >
                                  <SearchIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Descripción"
                          multiline
                          minRows={1}
                          value={it.descripcion}
                          onChange={(e) => handleItemChange(idx, { descripcion: e.target.value })}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Cantidad"
                          type="number"
                          value={Number.isFinite(it.cantidad) ? it.cantidad : 0}
                          inputProps={{ min: 0, step: 0.01 }}
                          onChange={(e) => handleItemChange(idx, { cantidad: parseFloat(e.target.value) || 0 })}
                        />
                        <ItemNotasTextField
                          fullWidth
                          label="Notas"
                          value={it.notas}
                          onChange={(notas) => handleItemChange(idx, { notas })}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
            </Stack>
          ) : (
            <TableContainer component={Paper} sx={{ mb: 2, maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Cantidad
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                        Sin ítems. Use &quot;Agregar item&quot; para añadir líneas.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((it, idx) => (
                    <TableRow key={it.rowId} hover sx={{ '& > td': { verticalAlign: 'top' } }}>
                      <TableCell sx={{ width: 170 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={it.codigoArticulo}
                          placeholder="Código"
                          onChange={(e) => {
                            clearRechazoDiscontinuado('COMPRAS', items[idx]?.codigoArticulo);
                            clearRechazoDiscontinuado('COMPRAS', e.target.value);
                            handleItemChange(idx, { codigoArticulo: e.target.value });
                          }}
                          onBlur={(e) => handleCodigoBlurOrEnter(idx, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value);
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setProductSearchIndex(idx);
                                    setOpenProductSearch(true);
                                  }}
                                >
                                  <SearchIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ minWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={1}
                          value={it.descripcion}
                          placeholder="Descripción"
                          onChange={(e) => handleItemChange(idx, { descripcion: e.target.value })}
                          sx={{
                            '& .MuiInputBase-inputMultiline': {
                              overflow: 'hidden',
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ width: 140 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={Number.isFinite(it.cantidad) ? it.cantidad : 0}
                          inputProps={{ min: 0, step: 0.01 }}
                          onChange={(e) => handleItemChange(idx, { cantidad: parseFloat(e.target.value) || 0 })}
                        />
                      </TableCell>

                      <TableCell sx={{ minWidth: 220 }}>
                        <ItemNotasTextField
                          fullWidth
                          value={it.notas}
                          placeholder="Notas"
                          onChange={(notas) => handleItemChange(idx, { notas })}
                        />
                      </TableCell>

                      <TableCell sx={{ width: 90 }} align="center">
                        <IconButton size="small" onClick={() => removeItem(it.rowId)} color="error" aria-label="Eliminar ítem">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 3, justifyContent: 'flex-start', '& > *': { width: { xs: '100%', sm: 'auto' } } }}
          >
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addItem}>
              Agregar item
            </Button>
            <Button variant="outlined" onClick={openBuscarPendientes} sx={{ color: '#1976d2', borderColor: '#1976d2' }}>
              Buscar pendientes
            </Button>
          </Stack>

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            justifyContent="flex-end"
            spacing={2}
            sx={{ '& > *': { width: { xs: '100%', sm: 'auto' } } }}
          >
            <Button variant="outlined" onClick={() => navigate('/compras/remitos')} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              fullWidth={isMobile}
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
            >
              {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Guardar'}
            </Button>
          </Stack>
          <NumeroInternoMeta nro={0} />
        </CardContent>
      </Card>

      <Dialog
        open={openProductSearch}
        onClose={() => {
          setOpenProductSearch(false);
          setProductSearchIndex(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Buscar artículo</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por código o descripción..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setProductosPage(0);
                  loadProductos();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setProductosPage(0);
                loadProductos();
              }}
              sx={{ color: '#1976d2', borderColor: '#1976d2' }}
            >
              Buscar
            </Button>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="center">Seleccionar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productosDisponibles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                      {productos.length === 0
                        ? 'No se encontraron artículos.'
                        : 'Los artículos de esta página ya están en el remito.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  productosDisponibles.map((p) => {
                    const disBloqueado = esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modoBloqueoDis);
                    const esDis = esTipoDiscontinuado(p.tipoArticulo);
                    return (
                  <TableRow key={p.codigoArticulo || p.codigo || ''} hover sx={{ opacity: disBloqueado ? 0.75 : 1 }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{p.codigoArticulo || p.codigo || '-'}</span>
                        {esDis && <Chip size="small" label="DIS" color="warning" />}
                      </Stack>
                    </TableCell>
                    <TableCell>{p.descripcion || p.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="center">
                      <Button size="small" disabled={disBloqueado} onClick={() => handleSelectProduct(p)}>
                        {disBloqueado ? 'No seleccionable' : 'Seleccionar'}
                      </Button>
                    </TableCell>
                  </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={productosTotalCount}
            page={productosPage}
            onPageChange={(_, p) => setProductosPage(p)}
            rowsPerPage={productosRowsPerPage}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenProductSearch(false);
              setProductSearchIndex(null);
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPendientes}
        onClose={() => setOpenPendientes(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
            pb: 1,
          }}
        >
          <Typography component="span" variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Buscar pendientes
          </Typography>
          <IconButton aria-label="Cerrar" onClick={() => setOpenPendientes(false)} edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
          {loadingPendientes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mb: 2, mt: 1 }}
                flexWrap="wrap"
                useFlexGap
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar por comprobante, código o descripción..."
                  value={pendientesBusqueda}
                  onChange={(e) => setPendientesBusqueda(e.target.value)}
                  sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth={isMobile}
                  label="Fecha emisión desde"
                  type="date"
                  value={pendientesFechaDesde}
                  onChange={(e) => setPendientesFechaDesde(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: '100%', sm: 170 } }}
                />
                <TextField
                  fullWidth={isMobile}
                  label="Fecha emisión hasta"
                  type="date"
                  value={pendientesFechaHasta}
                  onChange={(e) => setPendientesFechaHasta(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: '100%', sm: 170 } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={soloInventariados}
                      onChange={(e) => setSoloInventariados(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Solo inventariados"
                  sx={{ ml: 0, whiteSpace: 'nowrap' }}
                />
              </Stack>

              {pendientesFiltrados.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  {pendientes.length === 0
                    ? 'No hay líneas con pendiente de recibir para este proveedor.'
                    : pendientesDisponibles.length === 0
                      ? 'Las líneas pendientes ya fueron agregadas a este remito.'
                      : 'No hay pendientes que coincidan con los filtros aplicados.'}
                </Typography>
              ) : isMobile ? (
                <Stack spacing={1.5}>
                  {pendientesFiltrados.map((p) => (
                    <Card key={`${p.nroFormularioMaster}-${p.nroItemMaster}`} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                            <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                              {formatComprobanteMasterDisplay(p)}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setPendienteHiloInfo({
                                  nroFormularioMaster: p.nroFormularioMaster,
                                  nroItemMaster: p.nroItemMaster,
                                  resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                                })
                              }
                              aria-label="Información facturado y remitido"
                              sx={{ flexShrink: 0 }}
                            >
                              <InfoOutlinedIcon fontSize="small" color="info" />
                            </IconButton>
                          </Stack>

                          <Box
                            sx={(t) => ({
                              p: 1,
                              borderRadius: 1.5,
                              backgroundColor: t.palette.primary.light + '14',
                            })}
                          >
                            <Typography variant="caption" color="text.secondary" display="block">
                              Artículo
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: 'primary.main', wordBreak: 'break-word' }}
                            >
                              {p.codigoArticulo} - {p.descripcionArticulo}
                            </Typography>
                          </Box>

                          <Typography variant="caption" color="text.secondary">
                            Emisión: {formatFechaEmisionComprobanteMaster(p.fechaEmisionComprobanteMaster)}
                          </Typography>

                          <Stack direction="row" spacing={2} flexWrap="wrap">
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Pedida
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {p.cantidadPedida}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Pend. facturar
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {p.pendienteFacturar}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Pend. recibir
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {p.pendienteRecibir}
                              </Typography>
                            </Box>
                          </Stack>

                          <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            onClick={() => void agregarPendiente(p)}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                          >
                            Agregar
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Comprobante</TableCell>
                        <TableCell>Fecha emisión</TableCell>
                        <TableCell>Artículo</TableCell>
                        <TableCell align="right">Cant. pedida</TableCell>
                        <TableCell align="right">Pend. facturar</TableCell>
                        <TableCell align="right">Pend. recibir</TableCell>
                        <TableCell align="center" sx={{ width: 44 }} />
                        <TableCell align="center">Acción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendientesFiltrados.map((p) => (
                        <TableRow key={`${p.nroFormularioMaster}-${p.nroItemMaster}`}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatComprobanteMasterDisplay(p)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatFechaEmisionComprobanteMaster(p.fechaEmisionComprobanteMaster)}
                          </TableCell>
                          <TableCell>
                            {p.codigoArticulo} - {p.descripcionArticulo}
                          </TableCell>
                          <TableCell align="right">{p.cantidadPedida}</TableCell>
                          <TableCell align="right">{p.pendienteFacturar}</TableCell>
                          <TableCell align="right">{p.pendienteRecibir}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Ver documentos facturados y remitidos">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setPendienteHiloInfo({
                                    nroFormularioMaster: p.nroFormularioMaster,
                                    nroItemMaster: p.nroItemMaster,
                                    resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                                  })
                                }
                                aria-label="Información facturado y remitido"
                              >
                                <InfoOutlinedIcon fontSize="small" color="info" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Button size="small" onClick={() => void agregarPendiente(p)}>
                              Agregar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1,
            '& > *': { width: { xs: '100%', sm: 'auto' }, m: { xs: '0 !important', sm: undefined } },
          }}
        >
          <Button onClick={() => setOpenPendientes(false)} fullWidth={isMobile} sx={{ textTransform: 'none' }}>
            Cerrar
          </Button>
          <Button
            variant="contained"
            onClick={() => void agregarTodosPendientes()}
            disabled={pendientesFiltrados.length === 0 || loadingPendientes || agregandoTodos}
            fullWidth={isMobile}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {agregandoTodos ? 'Agregando…' : 'Agregar todos'}
          </Button>
        </DialogActions>
      </Dialog>

      <CompraPendienteHiloInfoDialog
        open={!!pendienteHiloInfo}
        onClose={() => setPendienteHiloInfo(null)}
        nroFormularioMaster={pendienteHiloInfo?.nroFormularioMaster ?? null}
        nroItemMaster={pendienteHiloInfo?.nroItemMaster ?? null}
        resumenLinea={pendienteHiloInfo?.resumenLinea}
      />
    </Box>
  );
};

export default RemitoCompraForm;
